import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useIntl, useLocation, useSelector, history, getLocale } from 'umi';
import { Guide } from '@/pages/home/components/Guide';
import {
  INTELLIGENCE_GUIDE_HIDE,
  INTELLIGENCE_GUIDE_BOX_LIST6,
} from '@/pages/home/components/GuideModule/GuideIntelligenceBox';
import withDelayWrapper from './DelayWrapper';
import {
  // GUIDE_SYS_INTELLIGENCE,
  GUIDE_TYPE_INTELLIGENCE_SOURCES_METRICS,
} from '@/pages/home/models/guide';
import { storage } from '@/utils/utils';
import { FROM_PREFIX, parseUserGuideStep, generateURLByUserGuideStep } from './utils';
// import { LANGUAGE_CN } from '@intelligence/_config/config';

import type { IGuideFrom } from './types';

import './index.scss';

export const SOURCES_METRICS_GUIDE_CLASSNAMES = {
  ELEMENT1: 'dbi-user-guide__menu-sources-metrics-sources',
  ELEMENT1_HIGHLIGHT1: 'dbi-user-guide__menu-sources-metrics-overview',
  ELEMENT1_HIGHLIGHT2: 'dbi-user-guide__menu-sources-metrics-metrics',
};

const STEP1_PATH = '/intelligence/sourcesMetrics/sources';
const STEP_PATH_LIST = [STEP1_PATH];
const USER_GUIDE_FROMS_CHARS = Array.from({ length: STEP_PATH_LIST.length - 1 }).map(
  (item, idx) => `${FROM_PREFIX}_${idx + 1}`,
);

const GuideSourcesMetricsContext = () => {
  const location: any = useLocation();
  const { pathname, query } = location;
  const { from } = query || {};
  const initialUserGuideStep = parseUserGuideStep(from);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  // const isCN = locale === LANGUAGE_CN;

  const dispatch = useDispatch();

  const { guideType, userInfo } = useSelector((state: any) => {
    const guideState = state?.guide;
    const homeState = state?.home;

    return {
      guideType: guideState?.guideType,
      userInfo: homeState?.userInfo,
    };
  });

  const userId = userInfo?.openid;

  const [currentGuideStep, setCurrentGuideStep] = useState(-1);

  const steps = useMemo<any[]>(
    () => [
      {
        highlightElements: [
          {
            element: document.querySelector(`.${SOURCES_METRICS_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(`.${SOURCES_METRICS_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT2}`),
          },
        ],
        element: () => document.querySelector(`.${SOURCES_METRICS_GUIDE_CLASSNAMES.ELEMENT1}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 6 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_6_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_6_1_desc' }),
        placement: 'right-end',
        bubbleStyle: {},
      },
    ],
    [locale],
  );

  const handleFinished = () => {
    if (userId) {
      let list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST6);
      list = Array.isArray(list) ? list : [];
      if (!list.includes(userId)) {
        list.push(userId);
      }
      storage.setLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST6, list);
    }
    dispatch({
      type: 'guide/showIntelligenceGuideModule',
      payload: true,
    });
  };

  // const handleCloseStep = () => {
  //   dispatch({
  //     type: 'guide/save',
  //     payload: {
  //       guideSys: GUIDE_SYS_INTELLIGENCE,
  //       guideType: '',
  //     },
  //   });
  // };

  const handleBack = (current: number) => {
    const prevPath = STEP_PATH_LIST[current];
    if (prevPath === pathname) {
      const path = generateURLByUserGuideStep(prevPath, current);
      history.replace(path);
      return;
    }
    history.goBack();
  };

  const handleNext = (current: number) => {
    if (current >= 0) {
      const { pathname } = location;
      const nextPathname = STEP_PATH_LIST[current];
      const routeAction =
        pathname === nextPathname ? 'replace' : 'push';
      const routePath = generateURLByUserGuideStep(nextPathname, current);
      history[routeAction](routePath);
    }
  };

  const handleCurrentChange = (current: number, context: { from: IGuideFrom }) => {
    const { from } = context || {};
    setCurrentGuideStep(current);
    if (from === 'back') {
      handleBack(current);
    } else if (from === 'next') {
      handleNext(current);
    } else if (from === 'finish') {
      handleFinished();
    } else if (from === 'cancel') {
      // handleCloseStep();
      handleFinished();
    }
  };

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType !== GUIDE_TYPE_INTELLIGENCE_SOURCES_METRICS) return;
    if (pathname !== STEP1_PATH && !USER_GUIDE_FROMS_CHARS.includes(from)) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [guideType, pathname, from, initialUserGuideStep]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST6);
    const guideHide = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_HIDE);
    if (!userId || list?.includes(userId) || guideHide) return;
    if (pathname !== STEP1_PATH && !USER_GUIDE_FROMS_CHARS.includes(from)) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [userId, pathname, from, initialUserGuideStep]);

  return (
    // finishText 根据中英文调整
    <Guide
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
      current={currentGuideStep}
      onCurrentChange={handleCurrentChange}
    />
  );
};

const GuideSourcesMetrics = () => {
  const location = useLocation();

  const { guideType } = useSelector((state: any) => {
    const guideState = state?.guide;
    return {
      guideType: guideState?.guideType,
    };
  });

  if (guideType !== GUIDE_TYPE_INTELLIGENCE_SOURCES_METRICS) return null;
  if (!STEP_PATH_LIST.includes(location.pathname)) return null;

  return <GuideSourcesMetricsContext />;
};

export const GuideSourcesMetricsDelay = withDelayWrapper(GuideSourcesMetrics);

export default GuideSourcesMetrics;
